import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useAuthUser } from '../../../contexts/useAuthUser';
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  onSnapshot,
  query,
  getDocs,
  deleteDoc,
  getDoc,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import * as ImagePicker from 'expo-image-picker';
import { firestore, storage } from '../../../services/firebase';
import { hasProductAccess } from '../../../utils/accessUtils';
import {
  isLegacyCommunityPost,
  isVirtualGeneralChannelSelected,
  VIRTUAL_GENERAL_CHANNEL_ID,
  VIRTUAL_GENERAL_CHANNEL_NAME,
} from '../../../utils/communityChannelUtils';

const CommunityContext = createContext();

const channelOrderKey = (id) =>
  id === VIRTUAL_GENERAL_CHANNEL_ID ? 'general' : id;

export const CommunityProvider = ({
  children,
  initialSelectedChannelId = null,
  focusPostId = null,
}) => {
  const [isPostModalVisible, setIsPostModalVisible] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [image, setImage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  // Sessão e documento `users/{uid}` vêm do AuthUserProvider (resolvidos no boot).
  const { user: authUserDoc, userId: authUserId, isAnonymous } = useAuthUser();
  const isUser = useMemo(
    () => (authUserDoc ? { id: authUserId, ...authUserDoc } : undefined),
    [authUserDoc, authUserId]
  );
  const [messageImageError, setMessageImageError] = useState(false);
  const [loadingCard, setLoadingCard] = useState(true);
  const [messageError, setMessageError] = useState(false);
  const [post, setPost] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [channelOrders, setChannelOrders] = useState({});

  const auth = getAuth();

  useEffect(() => {
    const ref = doc(firestore, 'communitySettings', 'channelOrder');
    const unsubscribe = onSnapshot(ref, (snap) => {
      setChannelOrders(snap.data()?.orders || {});
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!auth.currentUser?.uid) return;
    const channelsQuery = query(collection(firestore, 'communityChannels'));
    const unsubscribe = onSnapshot(channelsQuery, (snapshot) => {
      const fetchedChannels = snapshot.docs.map((channelDoc) => ({
        id: channelDoc.id,
        ...channelDoc.data(),
      }));
      const virtualChannel = {
        id: VIRTUAL_GENERAL_CHANNEL_ID,
        name: VIRTUAL_GENERAL_CHANNEL_NAME,
      };
      const withVirtual = [virtualChannel, ...fetchedChannels].sort(
        (a, b) =>
          (channelOrders[channelOrderKey(a.id)] ?? Number.MAX_SAFE_INTEGER) -
          (channelOrders[channelOrderKey(b.id)] ?? Number.MAX_SAFE_INTEGER)
      );
      setAllChannels(withVirtual);

      const availableChannels = isUser?.isAdmin
        ? withVirtual
        : withVirtual.filter((channel) => channel.id === VIRTUAL_GENERAL_CHANNEL_ID || hasProductAccess(channel, isUser || {}));

      setChannels(availableChannels);
      setSelectedChannelId((prevSelected) => {
        if (!availableChannels.length) return null;
        if (initialSelectedChannelId) {
          // Só aceita o canal vindo da navegação se o usuário tiver acesso a ele;
          // sem essa checagem um link direto abriria posts de canal restrito.
          const requested = availableChannels.find(
            (channel) => channel.id === initialSelectedChannelId
          );
          if (requested) {
            return requested.id;
          }
        }
        const stillExists = availableChannels.some((channel) => channel.id === prevSelected);
        return stillExists ? prevSelected : availableChannels[0].id;
      });
    });
    return () => unsubscribe();
  }, [isUser, initialSelectedChannelId, channelOrders]);

  const updatePost = (updatedPost) => {
    setPosts((prevPosts) => {
      const idx = prevPosts.findIndex((p) => p.id === updatedPost.id);
      if (idx === -1) {
        return prevPosts;
      }
      return prevPosts.map((prevPost) =>
        prevPost.id === updatedPost.id ? updatedPost : prevPost
      );
    });
  };

  // `subs` acumula os unsubscribes dos listeners aninhados (likes/comments/
  // replies); sem isso eles nunca são cancelados e se multiplicam a cada
  // emissão do snapshot de posts e a cada troca de canal.
  const wireRealtimePost = (docSnap, subs) => {
    const postData = docSnap.data();
    const post = {
      id: docSnap.id,
      ...postData,
      dataLikes: [],
      dataComments: [],
    };

    const postLikesRef = collection(docSnap.ref, 'likes');
    subs.push(onSnapshot(postLikesRef, (likesSnapshot) => {
      post.dataLikes = [];
      likesSnapshot.forEach((likeDoc) => {
        post.dataLikes.push({ id: likeDoc.id, ...likeDoc.data() });
      });
      updatePost(post);
    }));

    const postCommentsRef = collection(docSnap.ref, 'comments');
    subs.push(onSnapshot(postCommentsRef, (commentsSnapshot) => {
      post.dataComments = [];
      commentsSnapshot.forEach((commentDoc) => {
        const commentData = commentDoc.data();
        const comment = {
          id: commentDoc.id,
          ...commentData,
          replies: [],
        };

        const commentRepliesRef = collection(commentDoc.ref, 'replies');
        subs.push(onSnapshot(commentRepliesRef, (repliesSnapshot) => {
          comment.replies = [];
          repliesSnapshot.forEach((replyDoc) => {
            comment.replies.push({
              id: replyDoc.id,
              ...replyDoc.data(),
            });
          });
          updatePost(post);
        }));

        post.dataComments.push(comment);
      });
      updatePost(post);
    }));

    return post;
  };

  useEffect(() => {
    if (!auth.currentUser?.uid) {
      setPosts([]);
      setLoadingCard(false);
      return undefined;
    }

    if (!selectedChannelId) {
      setPosts([]);
      setLoadingCard(true);
      return undefined;
    }

    const viewingGeneral = isVirtualGeneralChannelSelected(selectedChannelId);

    setLoadingCard(true);

    const postsQuery = viewingGeneral
      ? query(collection(firestore, 'posts'))
      : query(collection(firestore, 'posts'), where('channelId', '==', selectedChannelId));

    let subs = [];
    const clearSubs = () => {
      subs.forEach((unsub) => unsub());
      subs = [];
    };

    const unsubscribe = onSnapshot(postsQuery, async (snapshot) => {
      // Reconecta os listeners aninhados do zero a cada emissão para não acumular.
      clearSubs();

      const docs = viewingGeneral
        ? snapshot.docs.filter((d) => isLegacyCommunityPost(d.data()))
        : snapshot.docs;

      const fetchedPosts = [];
      docs.forEach((dSnap) => {
        fetchedPosts.push(wireRealtimePost(dSnap, subs));
      });

      if (focusPostId && !fetchedPosts.some((p) => p.id === focusPostId)) {
        try {
          const refPost = doc(firestore, 'posts', focusPostId);
          const postSnap = await getDoc(refPost);
          if (postSnap.exists()) {
            fetchedPosts.push(wireRealtimePost(postSnap, subs));
          }
        } catch (e) {
          console.error('Community focus post load error:', e);
        }
      }

      fetchedPosts.sort(
        (a, b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0)
      );
      setPosts(fetchedPosts);
      setLoadingCard(false);
    });

    return () => {
      clearSubs();
      unsubscribe();
    };
  }, [selectedChannelId, focusPostId]);

  const openImagePicker = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 1,
      });

      if (!result.canceled && result.assets?.[0]?.uri) {
        setImage(result.assets[0].uri);
        setMessageImageError(false);
      }
    } catch (error) {
      console.error('Erro ao abrir seletor de imagem:', error);
    }
  };

  const closeModal = () => {
    setIsPostModalVisible(false);
    setTextContent('');
    setImage(null);
    setLoadingCard(false);
    setLoading(false);
  };

  const handlePost = async () => {
    try {
      if (!selectedChannelId) return;
      const currentChannel = allChannels.find((channel) => channel.id === selectedChannelId);
      if (currentChannel?.onlyAdminCanPost && !isUser?.isAdmin) {
        return;
      }
      if (!textContent.trim() && !image) {
        return;
      }
      setLoading(true);
      if (image) {
        const imageRef = ref(
          storage,
          `community/${Math.random().toString(36).substring(2)}`
        );
        const response = await fetch(image);
        const blob = await response.blob();

        const uploadTask = uploadBytesResumable(imageRef, blob);

        uploadTask.on(
          'state_changed',
          null,
          (error) => {
            console.error('Erro durante o upload da imagem:', error);
            setMessageError(true);
            setLoading(false);
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);

            await createPost(url);
          }
        );
      } else {
        await createPost(null);
      }
    } catch (error) {
      console.error('Erro ao criar o post:', error);
      setMessageError(true);
      setLoading(false);
    }
  };

  const createPost = async (imageUrl) => {
    try {
      const authorName =
        isUser?.Nome_Completo?.trim() ||
        isUser?.Email?.trim() ||
        auth.currentUser?.displayName?.trim() ||
        'User';

      const newPostData = {
        authorId: auth.currentUser.uid,
        contentText: textContent,
        createdAt: serverTimestamp(),
        nameUser: authorName,
      };

      if (!isVirtualGeneralChannelSelected(selectedChannelId)) {
        newPostData.channelId = selectedChannelId;
      }

      if (imageUrl) {
        newPostData.contentImage = imageUrl;
      }

      if (isUser.photo) {
        newPostData.photo = isUser.photo;
      }

      await addDoc(collection(firestore, 'posts'), newPostData);
      setLoading(false);
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar o post no Firestore:', error);
      setMessageError(true);
    } finally {
      setLoading(false);
    }
  };


  const filterPostForId = (id) => {
    if (!id) {
      setPost([]);
      return [];
    }
    const filteredPost = posts.filter((post) => post.id === id);

    if (filteredPost.length > 0) {
      const validComments = filteredPost[0].dataComments.filter(
        (comment) => comment.createdAt && comment.createdAt.seconds
      );

      const sortedComments = validComments.sort(
        (a, b) => b.createdAt.seconds - a.createdAt.seconds
      );

      const updatedPost = { ...filteredPost[0], dataComments: sortedComments };

      setPost([updatedPost]);
      return [updatedPost];
    }

    setPost([]);
    return [];
  };

  const handleCommentSubmit = async (comment, setComment, postId) => {
    try {
      if (!comment.trim()) {
        return;
      }
      const commentData = {
        userId: auth.currentUser.uid,
        comment: comment,
        createdAt: serverTimestamp(),
        userName: isUser.Nome_Completo,
      };

      const commentRef = collection(
        doc(firestore, 'posts', postId),
        'comments'
      );
      await addDoc(commentRef, commentData);
      setComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleReplySubmit = async (parentCommentId, replyText, postId) => {
    try {
      if (!replyText.trim()) {
        return;
      }
      const replyData = {
        userId: auth.currentUser.uid,
        reply: replyText,
        createdAt: serverTimestamp(),
        likes: 0,
        commentId: parentCommentId,
      };

      const commentRef = doc(
        firestore,
        'posts',
        postId,
        'comments',
        parentCommentId
      );
      const repliesCollectionRef = collection(commentRef, 'replies');
      await addDoc(repliesCollectionRef, replyData);
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const isAdminOrIsUserCurrent = (id) => {
    if (isUser.id === id || isUser.isAdmin === true) {
      return true;
    }
    return false;
  };

  const handleDeleteComment = async (commentId, id) => {
    try {
      const postRef = doc(firestore, 'posts', id);

      const commentRef = doc(postRef, 'comments', commentId);

      const repliesQuerySnapshot = await getDocs(
        collection(commentRef, 'replies')
      );

      const deleteRepliesPromises = repliesQuerySnapshot.docs.map(
        async (replyDoc) => {
          await deleteDoc(replyDoc.ref);
        }
      );

      await Promise.all(deleteRepliesPromises);

      await deleteDoc(commentRef);
    } catch (error) {
      console.error('Erro ao excluir comentário e suas respostas:', error);
    }
  };

  const handleDeleteReply = async (commentId, replyId, id) => {
    try {
      const postRef = doc(firestore, 'posts', id);
      const commentRef = doc(postRef, 'comments', commentId);
      const replyRef = collection(commentRef, 'replies');
      const specificReplyRef = doc(replyRef, replyId);
      await deleteDoc(specificReplyRef);
    } catch (error) {
      console.error('Erro ao excluir resposta:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const likesRef = collection(firestore, 'posts', postId, 'likes');
      const likesQuerySnapshot = await getDocs(likesRef);
      likesQuerySnapshot.forEach(async (likeDoc) => {
        await deleteDoc(doc(firestore, 'posts', postId, 'likes', likeDoc.id));
      });

      const commentsRef = collection(firestore, 'posts', postId, 'comments');
      const commentsQuerySnapshot = await getDocs(commentsRef);
      commentsQuerySnapshot.forEach(async (commentDoc) => {
        const repliesRef = collection(
          firestore,
          'posts',
          postId,
          'comments',
          commentDoc.id,
          'replies'
        );

        const repliesQuerySnapshot = await getDocs(repliesRef);
        repliesQuerySnapshot.forEach(async (replyDoc) => {
          await deleteDoc(
            doc(
              firestore,
              'posts',
              postId,
              'comments',
              commentDoc.id,
              'replies',
              replyDoc.id
            )
          );
        });
        await deleteDoc(
          doc(firestore, 'posts', postId, 'comments', commentDoc.id)
        );
      });

      const postDocRef = doc(firestore, 'posts', postId);
      const postDocSnapshot = await getDoc(postDocRef);

      if (!postDocSnapshot.exists()) {
        console.error('Documento do post não encontrado');
        return;
      }

      const postData = postDocSnapshot.data();
      const imgUrl = postData.contentImage;

      if (imgUrl) {
        const imagePath = imgUrl.split('%2F')[1].split('?')[0];
        const storageRef = ref(storage, `community/${imagePath}`);
        await deleteObject(storageRef);
      } else {
        console.error('URL da imagem não encontrada no documento do post');
      }
      await deleteDoc(doc(firestore, 'posts', postId));
    } catch (error) {
      console.error('Erro ao excluir o post:', error);
    }
  };

  const createChannel = async (channelData) => {
    if (!isUser?.isAdmin) return;
    await addDoc(collection(firestore, 'communityChannels'), {
      ...channelData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const updateChannel = async (channelId, channelData) => {
    if (!isUser?.isAdmin) return;
    await updateDoc(doc(firestore, 'communityChannels', channelId), {
      ...channelData,
      updatedAt: serverTimestamp(),
    });
  };

  const moveChannel = async (channelId, direction) => {
    if (!isUser?.isAdmin) return;
    const idx = allChannels.findIndex((c) => c.id === channelId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx === -1 || newIdx < 0 || newIdx >= allChannels.length) return;
    const reordered = [...allChannels];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    const orders = {};
    reordered.forEach((c, i) => { orders[channelOrderKey(c.id)] = i; });
    await setDoc(doc(firestore, 'communitySettings', 'channelOrder'), { orders });
  };

  const deleteChannel = async (channelId) => {
    if (!isUser?.isAdmin) return;
    const postsSnapshot = await getDocs(
      query(collection(firestore, 'posts'), where('channelId', '==', channelId))
    );

    for (const postDoc of postsSnapshot.docs) {
      await handleDeletePost(postDoc.id);
    }

    await deleteDoc(doc(firestore, 'communityChannels', channelId));
  };

  const handleLike = async (item, auth) => {
    try {
      const filteredLikes = item.dataLikes?.some(
        (i) => i.userId === auth.currentUser.uid
      );

      if (!filteredLikes) {
        const likedRef = collection(doc(firestore, 'posts', item.id), 'likes');
        const likeData = {
          userId: auth.currentUser.uid,
          createdAt: serverTimestamp(),
          liked: true,
        };

        await addDoc(likedRef, likeData);
      } else {
        const postRef = doc(firestore, 'posts', item.id);
        const querySnapshot = await getDocs(collection(postRef, 'likes'));

        querySnapshot.forEach(async (doc) => {
          if (doc.data().userId === auth.currentUser.uid) {
            await deleteDoc(doc.ref);
          }
        });
      }
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  const selectedChannel = allChannels.find((channel) => channel.id === selectedChannelId);
  const canUserPostOnSelectedChannel =
    !!selectedChannel && (!selectedChannel.onlyAdminCanPost || isUser?.isAdmin);

  const contextValues = {
    isPostModalVisible,
    setIsPostModalVisible,
    textContent,
    setTextContent,
    image,
    setImage,
    posts,
    loading,
    isUser,
    openImagePicker,
    closeModal,
    handlePost,
    isAnonymous,
    messageError,
    setMessageError,
    messageImageError,
    loadingCard,
    setPosts,
    auth,
    filterPostForId,
    post,
    handleCommentSubmit,
    handleReplySubmit,
    isAdminOrIsUserCurrent,
    handleDeleteComment,
    handleDeleteReply,
    handleDeletePost,
    handleLike,
    channels,
    allChannels,
    selectedChannelId,
    setSelectedChannelId,
    createChannel,
    updateChannel,
    deleteChannel,
    moveChannel,
    canUserPostOnSelectedChannel,
  };

  return (
    <CommunityContext.Provider value={contextValues}>
      {children}
    </CommunityContext.Provider>
  );
};

export const useCommunity = () => useContext(CommunityContext);
